import type { ConnectedStandardSolanaWallet } from '@privy-io/react-auth/solana';
import type { useSignMessage, useSignTransaction } from '@privy-io/react-auth/solana';
import {
  address as parseSolanaAddress,
  appendTransactionMessageInstructions,
  type Base64EncodedWireTransaction,
  compileTransaction,
  createTransactionMessage,
  getBase58Decoder,
  getTransactionEncoder,
  pipe,
  setTransactionMessageFeePayer,
  setTransactionMessageLifetimeUsingBlockhash,
  type Signature,
} from '@solana/kit';
import type { PublicClient, WalletClient } from 'viem';

import { ClientWalletError } from '@/lib/errors/client';

import { CHAIN_IDS } from '@/constants/chain';

import type {
  DefinitiveEvmLimitOrderBuildResponse,
  DefinitiveLimitOrderPreviewResponse,
  DefinitiveSvmInstruction,
  DefinitiveSvmLimitOrderBuildResponse,
  LimitOrderBuildResponse,
  OneInchLimitOrderBuildResponse,
} from '@/utils/0x.types';
import { toSolanaInstruction } from '@/utils/definitive/solana';
import type { EIP712TypedData } from '@/utils/signature';
import { sleep } from '@/utils/sleep';
import { getSolanaRpc } from '@/utils/solana';

export type SubmitLimitOrderPhase =
  | 'switching-chain'
  | 'building'
  | 'approving'
  | 'signing'
  | 'submitting'
  | 'idle';

type SetPhase = (phase: SubmitLimitOrderPhase) => void;

export type EvmLimitOrderExecutionContext = {
  chainFamily: 'evm';
  chainId: number;
  takerAmount: bigint;
  walletClient: WalletClient;
  publicClient: Pick<PublicClient, 'waitForTransactionReceipt'> | undefined;
  setPhase: SetPhase;
};

export type SvmLimitOrderExecutionContext = {
  chainFamily: 'svm';
  chainId: typeof CHAIN_IDS.SOLANA;
  solanaWallet: ConnectedStandardSolanaWallet;
  signMessage: ReturnType<typeof useSignMessage>['signMessage'];
  signTransaction: ReturnType<typeof useSignTransaction>['signTransaction'];
  setPhase: SetPhase;
};

export type LimitOrderExecutionContext =
  | EvmLimitOrderExecutionContext
  | SvmLimitOrderExecutionContext;

export type SignableLimitOrderBuildResponse = Exclude<
  LimitOrderBuildResponse,
  DefinitiveLimitOrderPreviewResponse
>;

export type PreparedLimitOrderSubmission = {
  request: Record<string, unknown>;
  orderIdentifier?: string;
};

async function prepareOneInchLimitOrderSubmission(
  buildResponse: OneInchLimitOrderBuildResponse,
  context: EvmLimitOrderExecutionContext,
): Promise<PreparedLimitOrderSubmission> {
  if (!context.walletClient.account) {
    throw new ClientWalletError('Wallet account is unavailable');
  }
  context.setPhase('signing');
  const signature = await context.walletClient.signTypedData({
    account: context.walletClient.account,
    domain: buildResponse.typedData.domain,
    types: {
      Order: buildResponse.typedData.types.Order,
    },
    message: buildResponse.typedData.message,
    primaryType: 'Order',
  });

  return {
    orderIdentifier: buildResponse.orderHash,
    request: {
      provider: buildResponse.provider,
      orderHash: buildResponse.orderHash,
      signature,
      rawOrder: buildResponse.rawOrder,
      chainId: context.chainId,
      baseAmount: context.takerAmount.toString(),
    },
  };
}

async function prepareDefinitiveEvmLimitOrderSubmission(
  buildResponse: DefinitiveEvmLimitOrderBuildResponse,
  context: EvmLimitOrderExecutionContext,
): Promise<PreparedLimitOrderSubmission> {
  if (!context.walletClient.account) {
    throw new ClientWalletError('Wallet account is unavailable');
  }

  if (buildResponse.approveTx) {
    if (!context.publicClient) {
      throw new ClientWalletError('Public client is unavailable');
    }
    context.setPhase('approving');
    const approvalHash = await context.walletClient.sendTransaction({
      account: context.walletClient.account,
      chain: context.walletClient.chain,
      to: buildResponse.approveTx.to,
      data: buildResponse.approveTx.data,
    });
    const receipt = await context.publicClient.waitForTransactionReceipt({ hash: approvalHash });
    if (receipt.status !== 'success') {
      throw new Error('Definitive approval transaction reverted');
    }
  }

  context.setPhase('signing');
  let evmPermitSignature: string | undefined;
  if (buildResponse.permitTypedData) {
    const permitTypedData = JSON.parse(buildResponse.permitTypedData) as EIP712TypedData;
    evmPermitSignature = await context.walletClient.signTypedData({
      account: context.walletClient.account,
      ...permitTypedData,
    });

    // Keep the second wallet prompt visible after the first prompt closes.
    await sleep(500);
  }

  const orderTypedData = JSON.parse(buildResponse.orderTypedData) as EIP712TypedData;
  const userSignature = await context.walletClient.signTypedData({
    account: context.walletClient.account,
    ...orderTypedData,
  });

  return {
    request: {
      provider: buildResponse.provider,
      chainId: context.chainId,
      quoteId: buildResponse.quoteId,
      userSignature,
      ...(evmPermitSignature ? { evmPermitSignature } : {}),
    },
  };
}

async function signAndConfirmSolanaInstructions({
  instructions,
  context,
}: {
  instructions: DefinitiveSvmInstruction[];
  context: SvmLimitOrderExecutionContext;
}): Promise<void> {
  const rpc = getSolanaRpc();
  const { value: latestBlockhash } = await rpc
    .getLatestBlockhash({ commitment: 'confirmed' })
    .send();
  const transactionMessage = pipe(
    createTransactionMessage({ version: 0 }),
    (message) =>
      setTransactionMessageFeePayer(parseSolanaAddress(context.solanaWallet.address), message),
    (message) => setTransactionMessageLifetimeUsingBlockhash(latestBlockhash, message),
    (message) =>
      appendTransactionMessageInstructions(instructions.map(toSolanaInstruction), message),
  );
  const unsignedTransaction = new Uint8Array(
    getTransactionEncoder().encode(compileTransaction(transactionMessage)),
  );
  const { signedTransaction } = await context.signTransaction({
    wallet: context.solanaWallet,
    transaction: unsignedTransaction,
    chain: 'solana:mainnet',
  });
  const signature = await rpc
    .sendTransaction(
      Buffer.from(signedTransaction).toString('base64') as Base64EncodedWireTransaction,
      {
        encoding: 'base64',
        preflightCommitment: 'confirmed',
      },
    )
    .send();

  for (let attempt = 0; attempt < 60; attempt += 1) {
    const { value: statuses } = await rpc
      .getSignatureStatuses([signature as Signature], { searchTransactionHistory: true })
      .send();
    const status = statuses[0];
    if (status?.err) {
      throw new Error(`Definitive Solana setup transaction failed: ${JSON.stringify(status.err)}`);
    }
    if (status?.confirmationStatus === 'confirmed' || status?.confirmationStatus === 'finalized') {
      return;
    }
    await sleep(500);
  }
  throw new Error('Definitive Solana setup transaction confirmation timed out');
}

async function prepareDefinitiveSvmLimitOrderSubmission(
  buildResponse: DefinitiveSvmLimitOrderBuildResponse,
  context: SvmLimitOrderExecutionContext,
): Promise<PreparedLimitOrderSubmission> {
  const setupInstructions = [
    ...(buildResponse.ataSetupIxs ?? []),
    ...(buildResponse.wrapInstructions ?? []),
    ...(buildResponse.delegateIx ? [buildResponse.delegateIx] : []),
  ];
  if (setupInstructions.length > 0) {
    context.setPhase('approving');
    await signAndConfirmSolanaInstructions({ instructions: setupInstructions, context });
  }

  context.setPhase('signing');
  let svmSponsoredDelegateTx: string | undefined;
  if (buildResponse.sponsoredDelegateTx) {
    const { signedTransaction } = await context.signTransaction({
      wallet: context.solanaWallet,
      transaction: new Uint8Array(Buffer.from(buildResponse.sponsoredDelegateTx, 'base64')),
      chain: 'solana:mainnet',
    });
    svmSponsoredDelegateTx = Buffer.from(signedTransaction).toString('base64');
  }

  const { signature } = await context.signMessage({
    wallet: context.solanaWallet,
    message: new TextEncoder().encode(buildResponse.orderMessage),
  });
  if (signature.length !== 64) {
    throw new ClientWalletError('Solana wallet returned an invalid message signature');
  }

  return {
    request: {
      provider: buildResponse.provider,
      chainId: context.chainId,
      quoteId: buildResponse.quoteId,
      userSignature: getBase58Decoder().decode(signature),
      svmNonce: buildResponse.nonce,
      svmDeadline: buildResponse.deadline,
      ...(svmSponsoredDelegateTx ? { svmSponsoredDelegateTx } : {}),
    },
  };
}

/** Narrows a build response to one that carries a signing payload; previews are never submittable. */
export function isSignableLimitOrderBuild(
  buildResponse: LimitOrderBuildResponse,
): buildResponse is SignableLimitOrderBuildResponse {
  return buildResponse.provider === '1inch' || buildResponse.signable;
}

export async function prepareLimitOrderSubmission(
  buildResponse: LimitOrderBuildResponse,
  context: LimitOrderExecutionContext,
): Promise<PreparedLimitOrderSubmission> {
  if (!isSignableLimitOrderBuild(buildResponse)) {
    throw new ClientWalletError('A pricing preview cannot be signed; connect a wallet first');
  }
  switch (buildResponse.chainFamily) {
    case 'evm':
      if (context.chainFamily !== 'evm') {
        throw new Error(
          'Limit order build response chain family does not match the selected chain',
        );
      }
      switch (buildResponse.provider) {
        case '1inch':
          return prepareOneInchLimitOrderSubmission(buildResponse, context);
        case 'definitive':
          return prepareDefinitiveEvmLimitOrderSubmission(buildResponse, context);
        default: {
          const exhaustiveProvider: never = buildResponse;
          return exhaustiveProvider;
        }
      }
    case 'svm':
      if (context.chainFamily !== 'svm') {
        throw new Error(
          'Limit order build response chain family does not match the selected chain',
        );
      }
      return prepareDefinitiveSvmLimitOrderSubmission(buildResponse, context);
    default: {
      const exhaustiveFamily: never = buildResponse;
      return exhaustiveFamily;
    }
  }
}
